<script setup lang="ts">
import * as Sentry from "@sentry/nuxt";

const props = defineProps<{
  error: MaybeRefOrGetter<unknown>;
  clearError?: unknown;
}>();

const { $faro } = useNuxtApp();

type KnownError = {
  statusCode: number;
  message: string;
  stack: string;
  name?: string;
};

const isKnownError = (
  error: MaybeRefOrGetter<KnownError | unknown>,
  valueToCheck: string,
): error is MaybeRefOrGetter<KnownError> => {
  const errorValue = toValue(error);

  return Boolean(
    errorValue && typeof errorValue === "object" && valueToCheck in errorValue,
  );
};

const clearErrorIfPossible = () => {
  if (props.clearError && typeof props.clearError === "function") {
    props.clearError();
  }
};

const router = useRouter();
router.afterEach(() => {
  clearErrorIfPossible();
});

const isInDevelopmentEnvironment =
  useRuntimeConfig().public.nodeEnv === "development";

watch(
  () => toValue(props.error),
  (newError) => {
    if (!newError) return;

    if (
      // Don't capture 404 errors on Sentry
      isKnownError(newError, "statusCode") &&
      toValue(newError).statusCode === 404
    )
      return;

    // Triggered within the runtime
    Sentry.setTag("origin", "runtime");
    Sentry.captureException(newError);

    $faro?.api.pushError(newError as Error, {
      context: {
        origin: "runtime",
      },
    });
  },
  {
    immediate: true,
  },
);

const errorMessage = computed(() => {
  if (isKnownError(props.error, "message")) {
    return toValue(props.error).message;
  }

  return null;
});

const errorStack = computed(() => {
  if (isKnownError(props.error, "stack")) {
    return toValue(props.error).stack;
  }

  return null;
});

const isExpiredAuthSessionError = computed(() => {
  if (isKnownError(props.error, "name")) {
    return toValue(props.error).name === "ExpiredAuthSessionError";
  }

  return false;
});

const is404Error = computed(() => {
  if (isKnownError(props.error, "statusCode")) {
    return toValue(props.error).statusCode === 404;
  }

  return false;
});

const platformStore = usePlatformStore();
const platformIsInMaintenanceMode = computed(
  () => platformStore.platformIsInMaintenanceMode,
);
</script>

<template>
  <LayoutPageMaintenanceMode v-if="error && platformIsInMaintenanceMode" />
  <LayoutPageNotFound v-else-if="is404Error" />
  <BaseError
    v-else-if="error && !isExpiredAuthSessionError"
    title="An error occurred"
  >
    <template #action>
      <a
        href="/"
        class="base-button"
      >
        Return to home page
      </a>
      <button
        class="base-button mt-2 w-full"
        @click="clearErrorIfPossible"
      >
        Retry
      </button>
    </template>

    <section
      v-if="isInDevelopmentEnvironment"
      class="bg-gray-50 p-8 mt-4"
    >
      <template v-if="errorMessage">
        <h2 class="text-lg font-semibold">Error</h2>
        <div class="bg-gray-200 p-4 overflow-scroll">
          <pre> {{ errorMessage }} </pre>
        </div>
      </template>

      <template v-if="errorStack">
        <h2 class="text-lg font-semibold mt-4">Stack Trace</h2>
        <div class="bg-gray-200 p-4 overflow-scroll">
          <!-- eslint-disable-next-line vue/no-v-html -->
          <pre v-html="errorStack" />
        </div>
      </template>
    </section>
  </BaseError>
</template>

<style lang="postcss" scoped>
.base-button {
  @apply border border-gray-300 border-solid tracking-normal block font-normal;
  @apply transition-all hover:bg-gray-100;
}
</style>
